import { useEffect, useRef } from 'react';
import type { Stripe } from '@stripe/stripe-js';
import { isStripeRedirectResponse } from '@/types/guards';
import { ProviderEnum } from '@/types/common';
import { stripeService } from '@/services/stripe';
import type { StripeApmNextActionState } from '@/types/context';
import type { StripePaymentResponse } from '@/types/payment';
import { logError } from '@/utils/logger';

interface StripeApmNextActionFormProps {
  state: StripeApmNextActionState;
  // The root context Stripe instance, used as a fallback when the selected
  // method's sub-MID instance isn't registered (single-account publishers).
  fallbackStripe: Stripe | null;
}

/**
 * Dedicated `handleNextAction` driver for generic in-page APMs (e.g. Bizum)
 * whose finalize response comes back as `status: redirect` with NO hosted
 * `redirect_to_url` — the next action (banking-app approval / same-window
 * modal) completes in-page. Fully separate from <StripeCard3DSForm/> (card +
 * wallet 3DS): each action type owns its own driver and neither rides the
 * other's state.
 *
 * Instance selection: generic APMs create their PaymentIntent on the selected
 * method's sub-MID instance (registered by <StripeGenericPayForm/> via
 * `stripeService.initialize()`); using the root context `stripe` returns
 * "PaymentIntent not found" for multi-MID publishers. Falls back to the passed
 * `fallbackStripe` when no sub-MID instance is registered.
 */
export const StripeApmNextActionForm = ({ state, fallbackStripe }: StripeApmNextActionFormProps) => {
  const { active, params, handlePaymentSuccess, handlePaymentError } = state;
  const isProcessing = useRef(false);

  useEffect(() => {
    if (!active || !params || !isStripeRedirectResponse(params, ProviderEnum.STRIPE)) {
      return;
    }

    if (isProcessing.current) {
      return;
    }

    let stripeForNextAction = fallbackStripe;
    try {
      stripeForNextAction = stripeService.getStripe();
    } catch {
      // Sub-MID instance not initialized — keep the fallback context instance.
    }
    if (!stripeForNextAction) {
      return;
    }

    isProcessing.current = true;

    stripeForNextAction
      .handleNextAction({
        clientSecret: (params as StripePaymentResponse).stripe.clientSecret,
      })
      .then((result) => {
        if (result.error) {
          // Mirror the redirect-manager mapping: prefer the issuer
          // `decline_code`, fall back to the Stripe error `type`.
          const appChargeErrorType = stripeService.mapStripeErrorCode(result.error.decline_code ?? result.error.type);
          handlePaymentError?.(appChargeErrorType);
          return;
        }

        // `handleNextAction` resolves WITHOUT a top-level `error` even when the
        // action itself succeeds but the issuer then declines the charge. In
        // that case the PaymentIntent lands in a non-authorized terminal status
        // with the decline detail on `last_payment_error`. Treat anything that
        // isn't an authorized status as a failure so the decline is surfaced.
        const paymentIntent = result.paymentIntent;
        const isAuthorized =
          paymentIntent?.status === 'succeeded' ||
          paymentIntent?.status === 'requires_capture' ||
          paymentIntent?.status === 'processing';
        if (!isAuthorized) {
          const lastPaymentError = paymentIntent?.last_payment_error;
          const appChargeErrorType = stripeService.mapStripeErrorCode(
            lastPaymentError?.decline_code ?? lastPaymentError?.code ?? lastPaymentError?.type
          );
          handlePaymentError?.(appChargeErrorType);
          return;
        }

        handlePaymentSuccess?.();
      })
      .catch((error) => {
        logError('Stripe APM handleNextAction error', error);
        handlePaymentError?.();
      })
      .finally(() => {
        isProcessing.current = false;
      });
  }, [active, params, handlePaymentSuccess, handlePaymentError, fallbackStripe]);

  return null;
};
